import { PILLARS, pillarColor } from '../constants';
import { relativeTime } from '../lib/region';
import { useSettings } from '../settings';
import type { ArticleDTO } from '../types';
import { navLabel } from '../i18n';
import { navigate } from '../lib/router';
import { CoinIcon, EyeIcon, ShieldCheckIcon } from './icons';

export type CardVariant = 'lead' | 'rail' | 'grid';

export function VerifiedBadge({ className = '' }: { className?: string }) {
  const { t } = useSettings();
  return (
    <span
      className={`inline-flex items-center gap-1 text-emerald-500 ${className}`}
      title={t('verified')}
    >
      <ShieldCheckIcon className="size-3" />
      <span className="hidden font-mono text-[9px] uppercase tracking-[0.14em] sm:inline">
        {t('verified')}
      </span>
    </span>
  );
}

function Kicker({ article, size = 'base' }: { article: ArticleDTO; size?: 'base' | 'sm' }) {
  const { locale, t } = useSettings();
  const color = pillarColor(article.pillar);

  return (
    <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 font-mono uppercase tracking-[0.16em] ${size === 'sm' ? 'text-[9px]' : 'text-[10px]'}`}>
      <span style={{ color }}>{navLabel(locale, article.pillar)}</span>
      {article.subsection && (
        <>
          <span className="text-ink-dim" aria-hidden="true">/</span>
          <span className="text-ink-muted">{navLabel(locale, article.subsection)}</span>
        </>
      )}
      {article.machine_translated && (
        <span className="border border-rule px-1 py-px text-[8px] tracking-[0.12em] text-ink-dim">
          {t('autoTranslated')}
        </span>
      )}
    </div>
  );
}

export default function ArticleCard({
  article,
  variant = 'grid',
}: {
  article: ArticleDTO;
  variant?: CardVariant;
}) {
  const { t } = useSettings();
  const color = pillarColor(article.pillar);
  const href = `/article/${article.id}`;

  const open = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    navigate(href);
  };

  const titleClass =
    variant === 'lead'
      ? 'text-[1.75rem] leading-[1.08] sm:text-[2.5rem]'
      : variant === 'rail'
        ? 'text-lg leading-snug sm:text-xl'
        : 'text-base leading-snug sm:text-lg';

  return (
    <article
      className="group flex h-full flex-col border-t pt-3"
      style={{ borderTopColor: variant === 'lead' ? color : undefined }}
    >
      <a href={href} onClick={open} className="flex flex-1 flex-col">
        <Kicker article={article} size={variant === 'grid' ? 'sm' : 'base'} />

        <h3
          className={`mt-2 font-serif font-bold tracking-[-0.01em] text-ink ${titleClass} text-wrap-balance group-hover:underline group-hover:decoration-1 group-hover:underline-offset-4`}
        >
          {article.title}
        </h3>

        {variant !== 'grid' && (
          <p
            className={`mt-2 text-ink-muted ${
              variant === 'lead' ? 'line-clamp-4 text-[15px] leading-relaxed' : 'line-clamp-2 text-[13px] leading-relaxed'
            }`}
          >
            {article.content}
          </p>
        )}

        <div className="mt-auto flex items-center gap-3 pt-3 font-mono text-[9px] uppercase tracking-[0.12em] text-ink-dim">
          <span className="text-ink-muted">{article.agent.name}</span>
          <span aria-hidden="true">·</span>
          <time dateTime={article.created_at}>{relativeTime(article.created_at)}</time>
          <span className="ml-auto flex items-center gap-1">
            <EyeIcon className="size-3" />
            {article.view_count.toLocaleString()}
          </span>
          <VerifiedBadge />
        </div>
      </a>

      <div className="mt-2 flex items-center justify-between border-t border-rule/60 pt-2">
        <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.12em] text-ink-dim">
          <span className="size-1.5" style={{ backgroundColor: color }} aria-hidden="true" />
          {PILLARS.find((pillar) => pillar.id === article.pillar)?.label ?? article.pillar}
        </span>
        <button
          type="button"
          disabled
          title={t('tip')}
          className="inline-flex cursor-not-allowed items-center gap-1 rounded-sm border border-rule px-2 py-0.5 text-[9px] font-mono uppercase tracking-[0.12em] text-ink-dim"
        >
          <CoinIcon className="size-3" />
          {t('tip')}
        </button>
      </div>
    </article>
  );
}
